/**
 * Modo painel do cliente — todos os ATIVOS, de todas as unidades.
 *
 * SÃO DOIS PAINÉIS, e a divisão de trabalho é esta:
 *
 *   • esta — um cartão por ATIVO, de todas as unidades. É a tela de quem
 *     acompanha a operação inteira e precisa saber qual câmara, de qual
 *     loja, está ruim agora.
 *   • `SiteKiosk` — UMA unidade: agregados à esquerda, planta à direita. É
 *     a tela pendurada dentro da unidade, onde a pergunta é ONDE.
 *
 * O que faz esta funcionar de três metros:
 *
 *  • ALERTAS PRIMEIRO, E GRANDES. A faixa de cima responde a pergunta da
 *    tela inteira antes de qualquer cartão. Quando não há nada, ela diz
 *    isso com todas as letras em vez de ficar vazia: faixa vazia é ambígua
 *    entre "tudo bem" e "não carregou", e essa ambiguidade é o defeito
 *    clássico de painel.
 *
 *  • CADA CARTÃO DIZ A UNIDADE. Sem isso, "Câmara 01" em cima de "Câmara
 *    01" de outra loja manda a pessoa pro lugar errado.
 *
 *  • PIOR PRIMEIRO. A ordem é por gravidade, não alfabética: numa tela que
 *    não rola, o que fica de fora tem que ser o que menos importa.
 *
 *  • DADO VELHO NÃO PODE PARECER BOM. O estado de cada cartão é o PIOR
 *    entre o que os alertas dizem e o que a leitura mostra — sensor parado
 *    há 20 minutos que ainda não gerou alerta aparece como sem comunicação,
 *    não verde. De três metros ninguém tem como duvidar do verde.
 *
 *  • SÓ LEITURA, E SEM NAVEGAÇÃO — exceto sair. Numa tela pública todo
 *    controle é um jeito de alguém deixar o painel num estado do qual
 *    ninguém vai tirar; mas "sem navegação" não pode virar "sem saída".
 */

import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";

import { equipmentsApi } from "@/api/equipments";
import { RelogioDoPainel } from "@/components/RelogioDoPainel";
import { SairDoPainel } from "@/components/SairDoPainel";
import {
  ESTADO, ORDEM_DOS_ESTADOS, estadoDoAtivo, piorEstado, type Estado,
} from "@/lib/estadoDoAtivo";
import { evaluateTemperature, type SetpointConfig } from "@/lib/thresholds";
import { useAuth } from "@/contexts/AuthContext";
import {
  qk, useActiveAlerts, useEquipments, useSites, useTenants,
} from "@/hooks/queries";
import type { ActiveAlert, EquipmentSummary } from "@/types";

/** Quantos alertas a faixa lista antes de resumir o resto num contador. */
const ALERTAS_NA_FAIXA = 4;

/**
 * Teto de cartões desenhados — e de consultas.
 *
 * Cada cartão custa uma consulta de resumo, porque temperatura por ativo não
 * vem de lugar nenhum mais barato. Sem teto, um cliente com cem ativos faria
 * centenas de requisições por minuto numa tela que fica semanas aberta. E
 * cem cartões numa TV não se leem de três metros de qualquer forma: o teto
 * é tanto de custo quanto de legibilidade.
 *
 * O que fica de fora é o que menos importa, porque a ordem é por gravidade —
 * e o resto vira um contador por estado, que não deixa ninguém sumir calado.
 */
const LIMITE_DE_CARTOES = 24;

/** Mais lento que os 10s do app: aqui são N consultas numa tela permanente. */
const INTERVALO_MS = 20_000;

function umNumero(valor: number | null | undefined, casas = 1): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return "—";
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });
}

function temperaturaDe(s: EquipmentSummary | undefined): number | null {
  const v = s?.metrics.temp_internal ?? s?.metrics.temp_product ?? null;
  return typeof v === "number" ? v : null;
}

function desde(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h${String(min % 60).padStart(2, "0")}`;
  return `há ${Math.floor(h / 24)}d`;
}

export default function KioskTenantPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const auth = useAuth();
  const tenants = useTenants(auth.isSuperAdmin);
  const sites = useSites();
  const equipments = useEquipments();
  const alertas = useActiveAlerts();

  const unidades = useMemo(
    () => (sites.data ?? []).filter((s) => s.tenant_id === tenantId),
    [sites.data, tenantId]);

  const alertasPorAtivo = useMemo(() => {
    const mapa = new Map<string, ActiveAlert[]>();
    for (const a of alertas.data ?? []) {
      const lista = mapa.get(a.equipment_id) ?? [];
      lista.push(a);
      mapa.set(a.equipment_id, lista);
    }
    return mapa;
  }, [alertas.data]);

  /** Os ativos do cliente, já ordenados por gravidade. */
  const ordenados = useMemo(() => {
    const nomeDaUnidade = new Map(unidades.map((s) => [s.id, s.name]));
    const lista = (equipments.data ?? [])
      .filter((e) => e.active && nomeDaUnidade.has(e.site_id))
      .map((e) => ({
        id: e.id,
        nome: e.name,
        unidade: nomeDaUnidade.get(e.site_id) ?? "—",
        // Pré-ordenação pelos ALERTAS, que custam uma consulta só. O estado
        // final de cada cartão ainda considera a leitura; a ordem não, e é
        // um preço aceitável: silêncio de sensor vira `device_offline`, que
        // é alerta.
        estado: estadoDoAtivo(alertasPorAtivo.get(e.id) ?? []),
      }));
    lista.sort((a, b) => {
      const g = ORDEM_DOS_ESTADOS.indexOf(a.estado) - ORDEM_DOS_ESTADOS.indexOf(b.estado);
      if (g !== 0) return g;
      return a.unidade.localeCompare(b.unidade) || a.nome.localeCompare(b.nome);
    });
    return lista;
  }, [equipments.data, unidades, alertasPorAtivo]);

  const visiveis = ordenados.slice(0, LIMITE_DE_CARTOES);
  const restantes = ordenados.slice(LIMITE_DE_CARTOES);

  const resumos = useQueries({
    queries: visiveis.map((a) => ({
      queryKey: qk.equipmentSummary(a.id),
      queryFn: () => equipmentsApi.summary(a.id),
      refetchInterval: INTERVALO_MS,
      staleTime: INTERVALO_MS / 2,
    })),
  });

  const cartoes = visiveis.map((a, i) => {
    const resumo = resumos[i]?.data as EquipmentSummary | undefined;
    const leitura = evaluateTemperature(
      temperaturaDe(resumo),
      (resumo?.metadata as SetpointConfig) ?? {},
      resumo?.last_seen,
    );
    const daLeitura: Estado =
      leitura.status === "stale" ? "mudo"
      : leitura.status === "alert" ? "critico"
      : leitura.status === "warn" ? "atencao"
      : "ok";
    return {
      ...a,
      temperatura: temperaturaDe(resumo),
      detalhe: leitura.label,
      estado: piorEstado([a.estado, daLeitura]),
    };
  });

  const foraDoPrevisto = ordenados.filter((a) => a.estado !== "ok").length;

  const alertasDoCliente = useMemo(() => {
    const meus = new Set(ordenados.map((a) => a.id));
    const gravidade: Record<string, number> = {
      disaster: 4, critical: 3, warning: 2, info: 1,
    };
    return (alertas.data ?? [])
      .filter((a) => meus.has(a.equipment_id))
      .sort((a, b) => (gravidade[b.severity] ?? 0) - (gravidade[a.severity] ?? 0)
                      || +new Date(a.since) - +new Date(b.since));
  }, [alertas.data, ordenados]);

  const nomeDoCliente =
    tenants.data?.find((t) => t.id === tenantId)?.name
    ?? (alertas.data ?? []).find((a) => a.tenant_name)?.tenant_name
    ?? "Painel de unidades";

  if (!tenantId) return null;

  return (
    <div className="borda-segura flex h-dvh flex-col overflow-hidden bg-surface-50 dark:bg-slate-950">
      <header className="mb-5 flex shrink-0 flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold text-slate-900 dark:text-slate-100">
            {nomeDoCliente}
          </h1>
          <p className="mt-1 text-base text-slate-500 dark:text-slate-400">
            {unidades.length} {unidades.length === 1 ? "unidade" : "unidades"}
            {" · "}
            {ordenados.length}{" "}
            {ordenados.length === 1 ? "ativo monitorado" : "ativos monitorados"}
          </p>
        </div>
        <div className="flex items-start gap-4">
          <SairDoPainel para="/" />
          <RelogioDoPainel marca={alertas.dataUpdatedAt} />
        </div>
      </header>

      <FaixaDeAlertas
        alertas={alertasDoCliente}
        foraDoPrevisto={foraDoPrevisto}
        total={ordenados.length}
      />

      <div className="mt-5 min-h-0 flex-1 overflow-hidden">
        {cartoes.length === 0 ? (
          <p className="text-lg text-slate-400 dark:text-slate-500">
            Nenhum ativo cadastrado neste cliente.
          </p>
        ) : (
          <div className="grid auto-rows-min grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-4">
            {cartoes.map((c) => <CartaoDoAtivo key={c.id} {...c} />)}
          </div>
        )}
        {restantes.length > 0 && (
          // Ninguém some calado: o que não coube vira contagem por estado.
          <p className="mt-4 text-base text-slate-500 dark:text-slate-400">
            e mais {restantes.length} {restantes.length === 1 ? "ativo" : "ativos"}
            {" — "}
            {ORDEM_DOS_ESTADOS
              .map((e) => [e, restantes.filter((a) => a.estado === e).length] as const)
              .filter(([, n]) => n > 0)
              .map(([e, n]) => `${n} ${ESTADO[e].rotulo.toLowerCase()}`)
              .join(", ")}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A faixa que responde a tela inteira.
 *
 * Sem alerta ela NÃO desaparece — diz "nenhum alerta ativo" com o mesmo peso
 * visual. Faixa ausente é ambígua entre "está tudo bem" e "não carregou", e
 * quem passa na frente não tem como desempatar.
 */
function FaixaDeAlertas({
  alertas, foraDoPrevisto, total,
}: {
  alertas: ActiveAlert[];
  foraDoPrevisto: number;
  total: number;
}) {
  if (!alertas.length) {
    const { cor, Icone } = ESTADO.ok;
    return (
      <section className="flex shrink-0 items-center gap-4 rounded-2xl border border-surface-200 bg-white px-8 py-6 dark:border-slate-800 dark:bg-slate-900">
        <span
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white"
          style={{ background: cor }}
        >
          <Icone className="h-8 w-8" aria-hidden />
        </span>
        <div>
          <p className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            Nenhum alerta ativo
          </p>
          <p className="mt-0.5 text-base text-slate-500 dark:text-slate-400">
            {total} {total === 1 ? "ativo" : "ativos"} dentro do previsto
          </p>
        </div>
      </section>
    );
  }

  const mostrados = alertas.slice(0, ALERTAS_NA_FAIXA);
  const resto = alertas.length - mostrados.length;

  return (
    <section className="shrink-0 rounded-2xl border border-surface-200 bg-white px-8 py-6 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {/* O único número-herói da tela: dois heróis brigam, e de longe o
            olho escolhe um deles sozinho. */}
        <span className="text-5xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {foraDoPrevisto}
        </span>
        <span className="text-xl text-slate-600 dark:text-slate-300">
          {foraDoPrevisto === 1 ? "ativo fora do previsto" : "ativos fora do previsto"}
          <span className="text-slate-400 dark:text-slate-500"> de {total}</span>
        </span>
      </div>

      <ul className="space-y-2">
        {mostrados.map((a) => {
          const estado = estadoDoAtivo([a]);
          const { rotulo, cor, Icone } = ESTADO[estado];
          return (
            <li key={`${a.rule_id}:${a.equipment_id}`} className="flex items-center gap-3">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: cor }}
              >
                <Icone className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1 truncate text-lg text-slate-900 dark:text-slate-100">
                <span className="font-medium">{a.equipment_name}</span>
                <span className="text-slate-500 dark:text-slate-400"> · {rotulo}</span>
              </span>
              <span className="shrink-0 text-base tabular-nums text-slate-500 dark:text-slate-400">
                {desde(a.since)}
              </span>
            </li>
          );
        })}
      </ul>

      {resto > 0 && (
        <p className="mt-3 text-base text-slate-500 dark:text-slate-400">
          e mais {resto} {resto === 1 ? "alerta" : "alertas"}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * Um ativo. O que precisa ser lido de três metros, e nada além.
 *
 * Cor E forma E palavra: de longe o ícone é o que sobra, e numa foto do
 * painel, a palavra. E a UNIDADE vai junto — sem ela, "Câmara 01" em cima de
 * "Câmara 01" de outra loja manda a pessoa pro lugar errado.
 */
function CartaoDoAtivo({
  nome, unidade, temperatura, detalhe, estado,
}: {
  nome: string;
  unidade: string;
  temperatura: number | null;
  detalhe: string;
  estado: Estado;
}) {
  const { rotulo, cor, Icone } = ESTADO[estado];

  return (
    <section className="flex min-w-0 flex-col rounded-2xl border border-surface-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white"
          style={{ background: cor }}
        >
          <Icone className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold leading-tight text-slate-900 dark:text-slate-100">
            {nome}
          </h2>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">{unidade}</p>
        </div>
      </div>

      <div className="mt-2 text-4xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {temperatura === null ? "—" : `${umNumero(temperatura)} °C`}
      </div>

      <div className="mt-0.5 truncate text-sm text-slate-500 dark:text-slate-400">
        {estado === "ok" ? rotulo : `${rotulo} · ${detalhe}`}
      </div>
    </section>
  );
}
